export type SeasonChoice = "snow" | "sun" | "festival" | "any";

export type SunType = "resort" | "yacht";

export type AttributeKey =
  | "partyScore"
  | "natureScore"
  | "costOfLiving"
  | "earningPotential"
  | "conditionsQuality"
  | "culturalDepth"
  | "careerDevelopment"
  | "ukAccessibility"
  | "communityFeel"
  | "durationPreference";

export type Answer = {
  id: string;
  label: string;
  subLabel?: string;
  targets?: { attribute: AttributeKey; ideal: number; weight: number }[];
  seasonFilter?: SeasonChoice;
  sunType?: SunType;
};

export type Question = {
  id: string;
  order: number;
  prompt: string;
  hint?: string;
  answers: Answer[];
  doubleWeight?: boolean;
  showIf?: "sun-only";
};

export type Destination = {
  id: string;
  name: string;
  tagline: string;
  region: string;
  guideSlug: string;
  conditionsSlug?: string;
  jobsQuery: string;
  seasonType: "snow" | "sun" | "festival";
  sunCategory?: SunType;
  emoji: string;
  gradient: string;
  attributes: Record<AttributeKey, number>;
  pitchlines: Partial<Record<AttributeKey, string>>;
};

export const QUESTIONS: Question[] = [
  {
    id: "season",
    order: 1,
    prompt: "Snow or sun?",
    answers: [
      { id: "snow", label: "Snow", subLabel: "Ski, snowboard, mountains", seasonFilter: "snow" },
      { id: "sun", label: "Sun", subLabel: "Summer, beaches, islands", seasonFilter: "sun" },
      { id: "festival", label: "Festival", subLabel: "UK summer festival circuit", seasonFilter: "festival" },
      { id: "any", label: "Surprise me", subLabel: "Show me everything", seasonFilter: "any" },
    ],
  },
  {
    id: "sun-type",
    order: 2,
    prompt: "What kind of summer are you after?",
    showIf: "sun-only",
    answers: [
      { id: "resort", label: "Resort, beach, or hospitality", subLabel: "Hotels, bars, villas, watersports", sunType: "resort" },
      { id: "yacht", label: "Yacht crew", subLabel: "Superyachts, charter, deckhand or interior", sunType: "yacht" },
    ],
  },
  {
    id: "saturday-night",
    order: 3,
    prompt: "It\u2019s Saturday night. Where are you?",
    answers: [
      {
        id: "club",
        label: "In the club. The bigger the better",
        targets: [{ attribute: "partyScore", ideal: 10, weight: 1.0 }],
      },
      {
        id: "quiet-bar",
        label: "A quiet bar with people I\u2019ve known all season",
        targets: [
          { attribute: "partyScore", ideal: 5, weight: 0.8 },
          { attribute: "communityFeel", ideal: 8, weight: 0.7 },
        ],
      },
      {
        id: "house-party",
        label: "House party at someone\u2019s chalet or villa",
        targets: [{ attribute: "partyScore", ideal: 7, weight: 0.9 }],
      },
      {
        id: "early-night",
        label: "In bed by 10. I\u2019m on the first lift tomorrow",
        targets: [
          { attribute: "partyScore", ideal: 2, weight: 0.9 },
          { attribute: "conditionsQuality", ideal: 8, weight: 0.6 },
        ],
      },
    ],
  },
  {
    id: "work-earn",
    order: 4,
    prompt: "What\u2019s the deal with work?",
    answers: [
      {
        id: "graft",
        label: "Graft hard, bank everything. I\u2019m here to earn",
        targets: [{ attribute: "earningPotential", ideal: 9, weight: 1.0 }],
      },
      {
        id: "lifestyle",
        label: "Lighter hours. I want time to actually enjoy being there",
        targets: [
          { attribute: "earningPotential", ideal: 3, weight: 0.9 },
          { attribute: "natureScore", ideal: 6, weight: 0.4 },
        ],
      },
      {
        id: "intense-short",
        label: "Intense but short. Stack shifts, then disappear for days",
        targets: [
          { attribute: "earningPotential", ideal: 7, weight: 0.7 },
          { attribute: "durationPreference", ideal: 4, weight: 0.5 },
        ],
      },
    ],
  },
  {
    id: "cost",
    order: 5,
    prompt: "How much do you care about the cost of living?",
    answers: [
      {
        id: "cheap",
        label: "A lot. I want somewhere my money goes further",
        targets: [{ attribute: "costOfLiving", ideal: 9, weight: 1.0 }],
      },
      {
        id: "pay-more",
        label: "I\u2019ll pay more if the wages and tips outweigh it",
        targets: [
          { attribute: "costOfLiving", ideal: 3, weight: 0.6 },
          { attribute: "earningPotential", ideal: 8, weight: 0.7 },
        ],
      },
      {
        id: "experience",
        label: "Break even is fine. I\u2019m here for the experience",
        targets: [
          { attribute: "costOfLiving", ideal: 5, weight: 0.4 },
          { attribute: "culturalDepth", ideal: 7, weight: 0.6 },
        ],
      },
    ],
  },
  {
    id: "conditions",
    order: 6,
    prompt: "How important are the conditions?",
    answers: [
      {
        id: "essential",
        label: "Non-negotiable. The best snow, waves, or weather going",
        targets: [{ attribute: "conditionsQuality", ideal: 10, weight: 1.0 }],
      },
      {
        id: "decent",
        label: "Good enough to enjoy on my days off",
        targets: [{ attribute: "conditionsQuality", ideal: 5, weight: 0.7 }],
      },
      {
        id: "dont-care",
        label: "I barely notice. I\u2019m there for the people and the scene",
        targets: [
          { attribute: "conditionsQuality", ideal: 2, weight: 0.7 },
          { attribute: "partyScore", ideal: 6, weight: 0.4 },
        ],
      },
    ],
  },
  {
    id: "culture",
    order: 7,
    prompt: "Does it need to feel like somewhere genuinely different?",
    answers: [
      {
        id: "essential",
        label: "Yes. I want to come back seeing things differently",
        targets: [{ attribute: "culturalDepth", ideal: 10, weight: 1.0 }],
      },
      {
        id: "bonus",
        label: "Nice bonus, but the social scene matters more",
        targets: [
          { attribute: "culturalDepth", ideal: 4, weight: 0.6 },
          { attribute: "partyScore", ideal: 6, weight: 0.5 },
        ],
      },
      {
        id: "familiar",
        label: "I\u2019d rather be somewhere familiar where I hit the ground running",
        targets: [
          { attribute: "culturalDepth", ideal: 2, weight: 0.8 },
          { attribute: "ukAccessibility", ideal: 7, weight: 0.5 },
        ],
      },
    ],
  },
  {
    id: "day-off",
    order: 8,
    prompt: "Your ideal day off?",
    answers: [
      {
        id: "explore",
        label: "Exploring somewhere nobody else has found",
        targets: [
          { attribute: "natureScore", ideal: 10, weight: 1.0 },
          { attribute: "partyScore", ideal: 2, weight: 0.4 },
        ],
      },
      {
        id: "recover",
        label: "Recovering from last night with the people I work with",
        targets: [
          { attribute: "partyScore", ideal: 7, weight: 0.6 },
          { attribute: "communityFeel", ideal: 7, weight: 0.6 },
        ],
      },
      {
        id: "adventure",
        label: "Something physical. A big hike, a dive, a ride",
        targets: [
          { attribute: "natureScore", ideal: 9, weight: 0.9 },
          { attribute: "conditionsQuality", ideal: 6, weight: 0.4 },
        ],
      },
      {
        id: "nothing",
        label: "Pool, beach, book. Absolutely nothing",
        targets: [{ attribute: "natureScore", ideal: 6, weight: 0.6 }],
      },
    ],
  },
  {
    id: "career",
    order: 9,
    prompt: "Does this season need to count for something afterwards?",
    answers: [
      {
        id: "yes",
        label: "Yes. I want it on my CV and I want it to open doors",
        targets: [
          { attribute: "careerDevelopment", ideal: 10, weight: 1.0 },
          { attribute: "earningPotential", ideal: 7, weight: 0.4 },
        ],
      },
      {
        id: "no",
        label: "Not really. This is a life thing, not a career thing",
        targets: [
          { attribute: "careerDevelopment", ideal: 2, weight: 0.9 },
          { attribute: "culturalDepth", ideal: 6, weight: 0.4 },
        ],
      },
      {
        id: "both",
        label: "Both. Work hard, have it mean something, then enjoy the rest",
        targets: [{ attribute: "careerDevelopment", ideal: 7, weight: 0.7 }],
      },
    ],
  },
  {
    id: "accessibility",
    order: 10,
    prompt: "How do you feel about distance?",
    answers: [
      {
        id: "close",
        label: "Short flight. I want to be able to pop home if I need to",
        targets: [{ attribute: "ukAccessibility", ideal: 9, weight: 1.0 }],
      },
      {
        id: "anywhere",
        label: "I\u2019ll go anywhere on earth if it\u2019s worth it",
        targets: [
          { attribute: "ukAccessibility", ideal: 2, weight: 0.9 },
          { attribute: "culturalDepth", ideal: 6, weight: 0.4 },
        ],
      },
      {
        id: "moderate",
        label: "A few hours is fine, just not the other side of the world",
        targets: [{ attribute: "ukAccessibility", ideal: 5, weight: 0.6 }],
      },
    ],
  },
  {
    id: "community",
    order: 11,
    prompt: "Are you the type who goes back?",
    answers: [
      {
        id: "yes",
        label: "Yes. I want to know everyone by name by week three",
        targets: [{ attribute: "communityFeel", ideal: 10, weight: 1.0 }],
      },
      {
        id: "rotate",
        label: "I rotate. Too many places to try",
        targets: [
          { attribute: "communityFeel", ideal: 3, weight: 0.8 },
          { attribute: "culturalDepth", ideal: 6, weight: 0.4 },
        ],
      },
      {
        id: "crew",
        label: "I care more about the crew than the place",
        targets: [
          { attribute: "communityFeel", ideal: 7, weight: 0.7 },
          { attribute: "partyScore", ideal: 5, weight: 0.3 },
        ],
      },
    ],
  },
  {
    id: "duration",
    order: 12,
    prompt: "How long are you committing?",
    answers: [
      {
        id: "full",
        label: "Full season. Three to five months minimum",
        targets: [{ attribute: "durationPreference", ideal: 9, weight: 1.0 }],
      },
      {
        id: "medium",
        label: "A month or two. Enough to settle in but not forever",
        targets: [{ attribute: "durationPreference", ideal: 5, weight: 0.8 }],
      },
      {
        id: "short",
        label: "As short as possible. A few weeks, then out",
        targets: [{ attribute: "durationPreference", ideal: 2, weight: 0.9 }],
      },
    ],
  },
  {
    id: "priority",
    order: 13,
    prompt: "What\u2019s the one thing that would make you go back?",
    hint: "Pick one",
    doubleWeight: true,
    answers: [
      {
        id: "conditions",
        label: "Conditions that blew my mind",
        targets: [{ attribute: "conditionsQuality", ideal: 10, weight: 1.0 }],
      },
      {
        id: "people",
        label: "The people. A scene that felt like family",
        targets: [
          { attribute: "partyScore", ideal: 7, weight: 0.6 },
          { attribute: "communityFeel", ideal: 10, weight: 1.0 },
        ],
      },
      {
        id: "money",
        label: "The money. I came back with real savings",
        targets: [{ attribute: "earningPotential", ideal: 9, weight: 1.0 }],
      },
      {
        id: "culture",
        label: "A place that genuinely changed how I see things",
        targets: [{ attribute: "culturalDepth", ideal: 10, weight: 1.0 }],
      },
      {
        id: "career",
        label: "Career progression I couldn\u2019t get any other way",
        targets: [{ attribute: "careerDevelopment", ideal: 10, weight: 1.0 }],
      },
    ],
  },
];

const ALPS_GRADIENT = "linear-gradient(135deg, #1B2840 0%, #2A3F6A 60%, #7A8B6F 100%)";
const MED_GRADIENT = "linear-gradient(135deg, #1B2840 0%, #2D6B7F 60%, #D9A57A 100%)";
const JAPAN_GRADIENT = "linear-gradient(135deg, #1B2840 0%, #3A4B7A 60%, #C8D4E6 100%)";
const NA_GRADIENT = "linear-gradient(135deg, #1B2840 0%, #355F7A 60%, #6B9AB0 100%)";
const YACHT_GRADIENT = "linear-gradient(135deg, #1B2840 0%, #2A5A7A 60%, #E8D5B0 100%)";
const FESTIVAL_GRADIENT = "linear-gradient(135deg, #1B2840 0%, #5C3A6E 60%, #E6A94A 100%)";

export const DESTINATIONS: Destination[] = [
  {
    id: "meribel", name: "M\u00e9ribel", tagline: "The seasonaire classic",
    region: "France \u00b7 Three Valleys", guideSlug: "meribel", conditionsSlug: "meribel",
    jobsQuery: "season=snow&location=morzine",
    seasonType: "snow", emoji: "\ud83c\udfd4\ufe0f", gradient: ALPS_GRADIENT,
    attributes: { partyScore: 8, natureScore: 8, costOfLiving: 4, earningPotential: 6, conditionsQuality: 9, culturalDepth: 5, careerDevelopment: 6, ukAccessibility: 8, communityFeel: 8, durationPreference: 8 },
    pitchlines: {
      partyScore: "Ronnie's at 2am on a Tuesday. You'll understand when you get there.",
      conditionsQuality: "Three Valleys means you'll never ski the same run twice in a season.",
      natureScore: "Step off the groomed runs and it's just you and the Vanoise.",
      communityFeel: "The Three Valleys seasonaire family is real. You'll have mates for life.",
    },
  },
  {
    id: "val-disere", name: "Val d'Is\u00e8re", tagline: "High altitude, high energy",
    region: "France \u00b7 Espace Killy", guideSlug: "val-disere", conditionsSlug: "val-disere",
    jobsQuery: "season=snow&location=val-disere",
    seasonType: "snow", emoji: "\u2744\ufe0f", gradient: ALPS_GRADIENT,
    attributes: { partyScore: 9, natureScore: 8, costOfLiving: 3, earningPotential: 7, conditionsQuality: 10, culturalDepth: 5, careerDevelopment: 6, ukAccessibility: 7, communityFeel: 7, durationPreference: 9 },
    pitchlines: {
      partyScore: "The Folie Douce invented apr\u00e8s-ski as a lifestyle. This is the source.",
      conditionsQuality: "2,100m base. Snow from November to May, no questions asked.",
      earningPotential: "The season starts hard and stays there. If you want easy, look elsewhere.",
      ukAccessibility: "Geneva, transfer, ski pass. You can be on snow within four hours of Gatwick.",
    },
  },
  {
    id: "tignes", name: "Tignes", tagline: "Linked with Val d'Is\u00e8re",
    region: "France \u00b7 Espace Killy", guideSlug: "tignes", conditionsSlug: "tignes",
    jobsQuery: "season=snow&location=tignes",
    seasonType: "snow", emoji: "\u26f7\ufe0f", gradient: ALPS_GRADIENT,
    attributes: { partyScore: 7, natureScore: 8, costOfLiving: 5, earningPotential: 6, conditionsQuality: 10, culturalDepth: 4, careerDevelopment: 6, ukAccessibility: 7, communityFeel: 7, durationPreference: 8 },
    pitchlines: {
      conditionsQuality: "The glacier means you're skiing proper snow when everywhere else is slush.",
      natureScore: "The lake freezes solid and the backcountry goes on forever.",
      communityFeel: "Quieter than Val next door, but the seasonaire nights out still go late.",
      earningPotential: "Altitude keeps the guests coming and the shifts steady all winter.",
    },
  },
  {
    id: "courchevel", name: "Courchevel", tagline: "Three Valleys luxury",
    region: "France \u00b7 Three Valleys", guideSlug: "courchevel", conditionsSlug: "courchevel",
    jobsQuery: "season=snow&location=courchevel",
    seasonType: "snow", emoji: "\u2728", gradient: ALPS_GRADIENT,
    attributes: { partyScore: 5, natureScore: 8, costOfLiving: 2, earningPotential: 9, conditionsQuality: 9, culturalDepth: 6, careerDevelopment: 10, ukAccessibility: 8, communityFeel: 4, durationPreference: 9 },
    pitchlines: {
      earningPotential: "The clientele don't check prices. Your tip jar reflects that.",
      careerDevelopment: "Five-star service at altitude. Everything you learn here travels with you.",
      conditionsQuality: "Three Valleys access from a base that's groomed to perfection.",
      durationPreference: "Long hours in luxury. But the money makes every shift count.",
    },
  },
  {
    id: "verbier", name: "Verbier", tagline: "Steep, stylish, Swiss",
    region: "Switzerland \u00b7 4 Vall\u00e9es", guideSlug: "verbier", conditionsSlug: "verbier",
    jobsQuery: "season=snow&location=verbier",
    seasonType: "snow", emoji: "\ud83d\uddfb", gradient: ALPS_GRADIENT,
    attributes: { partyScore: 8, natureScore: 9, costOfLiving: 1, earningPotential: 9, conditionsQuality: 10, culturalDepth: 6, careerDevelopment: 8, ukAccessibility: 7, communityFeel: 6, durationPreference: 8 },
    pitchlines: {
      earningPotential: "Swiss wages in a town where the clientele doesn't blink at bills.",
      conditionsQuality: "Steep, deep, and genuinely world-class freeride terrain.",
      partyScore: "Farm Club, Pub Mont Fort, apr\u00e8s that doesn't pretend to be casual.",
      natureScore: "The view from Mont Fort alone is worth a season.",
    },
  },
  {
    id: "zermatt", name: "Zermatt", tagline: "The Matterhorn resort",
    region: "Switzerland \u00b7 Matterhorn", guideSlug: "zermatt", conditionsSlug: "zermatt",
    jobsQuery: "season=snow&location=zermatt",
    seasonType: "snow", emoji: "\u26f0\ufe0f", gradient: ALPS_GRADIENT,
    attributes: { partyScore: 4, natureScore: 10, costOfLiving: 2, earningPotential: 8, conditionsQuality: 10, culturalDepth: 7, careerDevelopment: 7, ukAccessibility: 6, communityFeel: 4, durationPreference: 8 },
    pitchlines: {
      natureScore: "The Matterhorn on your doorstep every single morning.",
      conditionsQuality: "Glacier skiing year-round. The snow here is a given, not a hope.",
      culturalDepth: "No cars, cobbled streets, and a culture that hasn't been watered down.",
      earningPotential: "Swiss hospitality wages with fewer distractions to spend them on.",
    },
  },
  {
    id: "chamonix", name: "Chamonix", tagline: "The birthplace of alpinism",
    region: "France \u00b7 Mont Blanc", guideSlug: "chamonix", conditionsSlug: "chamonix",
    jobsQuery: "season=snow&location=chamonix",
    seasonType: "snow", emoji: "\ud83e\uddd7", gradient: ALPS_GRADIENT,
    attributes: { partyScore: 6, natureScore: 10, costOfLiving: 4, earningPotential: 6, conditionsQuality: 9, culturalDepth: 6, careerDevelopment: 7, ukAccessibility: 8, communityFeel: 7, durationPreference: 7 },
    pitchlines: {
      natureScore: "Mont Blanc on one side, the Aiguilles on the other. It never gets old.",
      conditionsQuality: "The Grands Montets and the Vall\u00e9e Blanche. Enough said.",
      careerDevelopment: "The birthplace of alpinism. Being here puts you in serious mountain company.",
      communityFeel: "Chambre Neuf and MBC keep the town honest after dark.",
    },
  },
  {
    id: "morzine", name: "Morzine", tagline: "Tight-knit British scene",
    region: "France \u00b7 Portes du Soleil", guideSlug: "morzine", conditionsSlug: "morzine",
    jobsQuery: "season=snow&location=morzine",
    seasonType: "snow", emoji: "\ud83c\uddeb\ud83c\uddf7", gradient: ALPS_GRADIENT,
    attributes: { partyScore: 7, natureScore: 8, costOfLiving: 6, earningPotential: 5, conditionsQuality: 7, culturalDepth: 5, careerDevelopment: 5, ukAccessibility: 9, communityFeel: 9, durationPreference: 7 },
    pitchlines: {
      communityFeel: "Tight British scene. Everyone knows everyone by week two.",
      ukAccessibility: "An hour from Geneva. You'll be home for Christmas lunch if you need to be.",
      costOfLiving: "Lower cost of living than the big-name resorts, same lift pass access.",
      conditionsQuality: "Portes du Soleil is vast. You won't run out of terrain in a season.",
    },
  },
  {
    id: "les-gets", name: "Les Gets", tagline: "Morzine's quieter neighbour",
    region: "France \u00b7 Portes du Soleil", guideSlug: "les-gets", conditionsSlug: "les-gets",
    jobsQuery: "season=snow&location=les-gets",
    seasonType: "snow", emoji: "\ud83c\udf32", gradient: ALPS_GRADIENT,
    attributes: { partyScore: 4, natureScore: 8, costOfLiving: 6, earningPotential: 5, conditionsQuality: 7, culturalDepth: 5, careerDevelopment: 5, ukAccessibility: 9, communityFeel: 7, durationPreference: 7 },
    pitchlines: {
      natureScore: "Tree-lined runs, a proper village square, and space to hear yourself think.",
      ukAccessibility: "Same easy Geneva transfer, half the crowds.",
      costOfLiving: "Lower rents than the bigger resorts, same lift pass.",
      conditionsQuality: "Tree runs for days when the visibility drops everywhere else.",
    },
  },
  {
    id: "st-anton", name: "St Anton", tagline: "The party resort",
    region: "Austria \u00b7 Arlberg", guideSlug: "st-anton", conditionsSlug: "st-anton",
    jobsQuery: "season=snow&location=st-anton",
    seasonType: "snow", emoji: "\ud83c\udf7b", gradient: ALPS_GRADIENT,
    attributes: { partyScore: 10, natureScore: 8, costOfLiving: 4, earningPotential: 6, conditionsQuality: 9, culturalDepth: 6, careerDevelopment: 5, ukAccessibility: 7, communityFeel: 7, durationPreference: 8 },
    pitchlines: {
      partyScore: "The Krazy Kanguruh at 3pm. St Anton wrote the book on apr\u00e8s-ski.",
      conditionsQuality: "Consistent Austrian snowfall and terrain that rewards the adventurous.",
      communityFeel: "Austrian hospitality hours are long, but the rhythm suits grafters.",
      natureScore: "The Arlberg is beautiful enough to make you forget how hard you partied.",
    },
  },
  {
    id: "whistler", name: "Whistler", tagline: "The biggest ski resort in North America",
    region: "Canada \u00b7 British Columbia", guideSlug: "whistler", conditionsSlug: "whistler",
    jobsQuery: "season=snow&location=whistler",
    seasonType: "snow", emoji: "\ud83c\udde8\ud83c\udde6", gradient: NA_GRADIENT,
    attributes: { partyScore: 8, natureScore: 10, costOfLiving: 3, earningPotential: 7, conditionsQuality: 9, culturalDepth: 6, careerDevelopment: 7, ukAccessibility: 3, communityFeel: 9, durationPreference: 9 },
    pitchlines: {
      natureScore: "Old-growth forest, bears in spring, and mountains that make the Alps feel small.",
      conditionsQuality: "The biggest ski area in North America. Over 8 metres of annual snow.",
      communityFeel: "International seasonaires from everywhere, but you'll all feel like locals by January.",
      careerDevelopment: "Canadian work culture is professional. This looks good on anyone's CV.",
    },
  },
  {
    id: "niseko", name: "Niseko", tagline: "Japow capital",
    region: "Japan \u00b7 Hokkaido", guideSlug: "niseko", conditionsSlug: "niseko",
    jobsQuery: "season=snow&location=niseko",
    seasonType: "snow", emoji: "\ud83d\uddfe", gradient: JAPAN_GRADIENT,
    attributes: { partyScore: 5, natureScore: 9, costOfLiving: 5, earningPotential: 7, conditionsQuality: 10, culturalDepth: 10, careerDevelopment: 7, ukAccessibility: 2, communityFeel: 6, durationPreference: 7 },
    pitchlines: {
      conditionsQuality: "15 metres of annual snowfall. The lightest powder on earth, and it's consistent.",
      culturalDepth: "Hot springs after powder days. Ramen at midnight. Nothing else compares.",
      earningPotential: "Australian wages with Japanese cost of living. The maths works.",
      natureScore: "Hokkaido in winter is another planet. Quiet, white, otherworldly.",
    },
  },
  {
    id: "ibiza", name: "Ibiza", tagline: "The nightlife island",
    region: "Spain \u00b7 Balearics", guideSlug: "ibiza", jobsQuery: "season=sun&location=ibiza",
    seasonType: "sun", sunCategory: "resort", emoji: "\ud83c\udf34", gradient: MED_GRADIENT,
    attributes: { partyScore: 10, natureScore: 7, costOfLiving: 3, earningPotential: 8, conditionsQuality: 7, culturalDepth: 5, careerDevelopment: 6, ukAccessibility: 9, communityFeel: 6, durationPreference: 7 },
    pitchlines: {
      partyScore: "The nightlife capital of Europe and everyone on the island knows it.",
      earningPotential: "Peak season tips in the super-clubs are a different category entirely.",
      ukAccessibility: "Two hours from most UK airports. You'll be back for bank holidays.",
      durationPreference: "Long shifts, late finishes, but the island makes every day off worth it.",
    },
  },
  {
    id: "mallorca", name: "Mallorca", tagline: "More than just Palma",
    region: "Spain \u00b7 Balearics", guideSlug: "mallorca", jobsQuery: "season=sun&location=mallorca",
    seasonType: "sun", sunCategory: "resort", emoji: "\ud83c\udfd6\ufe0f", gradient: MED_GRADIENT,
    attributes: { partyScore: 7, natureScore: 8, costOfLiving: 5, earningPotential: 6, conditionsQuality: 7, culturalDepth: 6, careerDevelopment: 6, ukAccessibility: 9, communityFeel: 5, durationPreference: 7 },
    pitchlines: {
      natureScore: "The Tramuntana mountains, hidden coves, and 300 days of sun.",
      partyScore: "Palma after dark has more range than people give it credit for.",
      ukAccessibility: "Cheap flights, short transfer, easy to pop home on days off.",
      costOfLiving: "Spanish cost of living with better weather than anywhere else in range.",
    },
  },
  {
    id: "mykonos", name: "Mykonos", tagline: "Luxury Greek islands",
    region: "Greece \u00b7 Cyclades", guideSlug: "mykonos", jobsQuery: "season=sun&location=greece",
    seasonType: "sun", sunCategory: "resort", emoji: "\ud83c\uddec\ud83c\uddf7", gradient: MED_GRADIENT,
    attributes: { partyScore: 9, natureScore: 8, costOfLiving: 2, earningPotential: 8, conditionsQuality: 8, culturalDepth: 6, careerDevelopment: 7, ukAccessibility: 7, communityFeel: 5, durationPreference: 7 },
    pitchlines: {
      partyScore: "Scorpios, Nammos, sunset cocktails that cost more than dinner. It's that island.",
      earningPotential: "Luxury clientele tip like they don't notice the zeros.",
      conditionsQuality: "The Cyclades wind keeps it bearable when everywhere else is melting.",
      careerDevelopment: "Peak season is relentless. But you're in the Aegean, so it balances.",
    },
  },
  {
    id: "corfu", name: "Corfu", tagline: "The British favourite",
    region: "Greece \u00b7 Ionian Islands", guideSlug: "corfu", jobsQuery: "season=sun&location=greece",
    seasonType: "sun", sunCategory: "resort", emoji: "\ud83e\uded2", gradient: MED_GRADIENT,
    attributes: { partyScore: 6, natureScore: 8, costOfLiving: 7, earningPotential: 5, conditionsQuality: 7, culturalDepth: 6, careerDevelopment: 5, ukAccessibility: 8, communityFeel: 7, durationPreference: 6 },
    pitchlines: {
      ukAccessibility: "Direct flights from everywhere. The easiest Greek island to reach.",
      costOfLiving: "Greek prices without the tourist markup of the Cyclades.",
      communityFeel: "British reps and bar staff who've been coming back for years. You'll fit right in.",
      culturalDepth: "Venetian old town, local tavernas, and a pace that forces you to slow down.",
    },
  },
  {
    id: "croatia", name: "Croatia", tagline: "Adriatic bluewater",
    region: "Croatia \u00b7 Dalmatian Coast", guideSlug: "croatia", jobsQuery: "season=sun&location=croatia",
    seasonType: "sun", sunCategory: "resort", emoji: "\u26f5", gradient: MED_GRADIENT,
    attributes: { partyScore: 8, natureScore: 9, costOfLiving: 6, earningPotential: 6, conditionsQuality: 8, culturalDepth: 7, careerDevelopment: 6, ukAccessibility: 7, communityFeel: 5, durationPreference: 6 },
    pitchlines: {
      natureScore: "The Adriatic coast. Crystal water, walled cities, and islands on your doorstep.",
      partyScore: "Split and Hvar keep things lively. The yacht crowd brings the energy.",
      culturalDepth: "A thousand years of history at every anchorage.",
      conditionsQuality: "Reliable summer winds and warm, clear water all season.",
    },
  },
  {
    id: "montenegro", name: "Montenegro", tagline: "The Adriatic's rising star",
    region: "Montenegro \u00b7 Bay of Kotor", guideSlug: "montenegro", jobsQuery: "season=sun&location=montenegro",
    seasonType: "sun", sunCategory: "resort", emoji: "\ud83c\udfd4\ufe0f", gradient: MED_GRADIENT,
    attributes: { partyScore: 5, natureScore: 10, costOfLiving: 8, earningPotential: 6, conditionsQuality: 8, culturalDepth: 7, careerDevelopment: 6, ukAccessibility: 6, communityFeel: 4, durationPreference: 6 },
    pitchlines: {
      natureScore: "The Bay of Kotor is a fjord in the Mediterranean. Genuinely breathtaking.",
      costOfLiving: "One of the cheapest coastlines in Europe with some of the best scenery.",
      culturalDepth: "Still feels undiscovered. Old Balkan charm without the tourist polish.",
      earningPotential: "The luxury marina scene is growing fast. Early movers benefit.",
    },
  },
  {
    id: "sardinia", name: "Sardinia", tagline: "Italian luxury coastline",
    region: "Italy \u00b7 Costa Smeralda", guideSlug: "sardinia", jobsQuery: "season=sun&location=italy",
    seasonType: "sun", sunCategory: "resort", emoji: "\ud83c\uddee\ud83c\uddf9", gradient: MED_GRADIENT,
    attributes: { partyScore: 5, natureScore: 9, costOfLiving: 3, earningPotential: 8, conditionsQuality: 8, culturalDepth: 7, careerDevelopment: 8, ukAccessibility: 6, communityFeel: 4, durationPreference: 7 },
    pitchlines: {
      earningPotential: "Costa Smeralda money is old money. The service standard pays accordingly.",
      natureScore: "Caribbean-blue water, white sand, and granite coastline. In Europe.",
      careerDevelopment: "Italian luxury hospitality on your CV opens doors across the Med.",
      culturalDepth: "High season is intense, but the shoulder weeks are yours.",
    },
  },
  {
    id: "lake-garda", name: "Lake Garda", tagline: "Alps meet the Med",
    region: "Italy \u00b7 Northern Lakes", guideSlug: "lake-garda", jobsQuery: "season=sun&location=italy",
    seasonType: "sun", sunCategory: "resort", emoji: "\ud83d\udea4", gradient: MED_GRADIENT,
    attributes: { partyScore: 4, natureScore: 10, costOfLiving: 5, earningPotential: 5, conditionsQuality: 8, culturalDepth: 7, careerDevelopment: 6, ukAccessibility: 7, communityFeel: 5, durationPreference: 6 },
    pitchlines: {
      natureScore: "Alps drop straight into a lake. Sunrise here makes everything else forgettable.",
      conditionsQuality: "Reliable thermal winds every afternoon. Sailors and windsurfers come for a reason.",
      culturalDepth: "Italian lakeside villages, aperitivo culture, and the Dolomites an hour away.",
      costOfLiving: "Northern Italy without the Milan price tag.",
    },
  },
  {
    id: "tenerife", name: "Tenerife", tagline: "Year-round season",
    region: "Spain \u00b7 Canary Islands", guideSlug: "tenerife", jobsQuery: "season=sun&location=tenerife",
    seasonType: "sun", sunCategory: "resort", emoji: "\ud83c\udf0b", gradient: MED_GRADIENT,
    attributes: { partyScore: 7, natureScore: 8, costOfLiving: 7, earningPotential: 5, conditionsQuality: 7, culturalDepth: 6, careerDevelopment: 5, ukAccessibility: 8, communityFeel: 6, durationPreference: 5 },
    pitchlines: {
      ukAccessibility: "Four-hour flight, year-round warmth. No off-season, no dead months.",
      partyScore: "The south coast doesn't sleep. Year-round nightlife with no off-season lull.",
      costOfLiving: "Canary Island prices make your money stretch further than mainland Spain.",
      conditionsQuality: "Waves on the north coast, sunshine guaranteed on the south.",
    },
  },
  {
    id: "antibes", name: "Antibes", tagline: "The Med's yacht hub",
    region: "France \u00b7 C\u00f4te d'Azur", guideSlug: "antibes", jobsQuery: "season=sun&role=yacht-stewardess",
    seasonType: "sun", sunCategory: "yacht", emoji: "\u2693", gradient: YACHT_GRADIENT,
    attributes: { partyScore: 6, natureScore: 7, costOfLiving: 2, earningPotential: 10, conditionsQuality: 8, culturalDepth: 6, careerDevelopment: 10, ukAccessibility: 7, communityFeel: 7, durationPreference: 8 },
    pitchlines: {
      earningPotential: "Tax-free salary, tips that dwarf the base, and no rent to pay.",
      careerDevelopment: "Every boat in the Med passes through. Your network builds itself.",
      communityFeel: "Charter weeks are brutal. But the between-charter downtime is yours.",
      culturalDepth: "Old town Antibes on a Tuesday evening when the boats are quiet.",
    },
  },
  {
    id: "palma", name: "Palma", tagline: "Spanish superyacht capital",
    region: "Spain \u00b7 Mallorca", guideSlug: "palma", jobsQuery: "season=sun&role=yacht-stewardess",
    seasonType: "sun", sunCategory: "yacht", emoji: "\u26f5", gradient: YACHT_GRADIENT,
    attributes: { partyScore: 7, natureScore: 8, costOfLiving: 5, earningPotential: 9, conditionsQuality: 8, culturalDepth: 6, careerDevelopment: 10, ukAccessibility: 8, communityFeel: 7, durationPreference: 8 },
    pitchlines: {
      careerDevelopment: "The refit capital. You'll cross paths with every crew in the industry.",
      earningPotential: "Spanish base with super-yacht tips. The combination works.",
      partyScore: "Santa Catalina on a night off with your crew. Palma knows how to go out.",
      ukAccessibility: "Two hours from London. You can be home for the weekend.",
    },
  },
  {
    id: "fort-lauderdale", name: "Fort Lauderdale", tagline: "The US yachting capital",
    region: "USA \u00b7 Florida", guideSlug: "fort-lauderdale", jobsQuery: "season=sun&role=yacht-stewardess",
    seasonType: "sun", sunCategory: "yacht", emoji: "\ud83c\uddfa\ud83c\uddf8", gradient: YACHT_GRADIENT,
    attributes: { partyScore: 6, natureScore: 6, costOfLiving: 4, earningPotential: 10, conditionsQuality: 7, culturalDepth: 5, careerDevelopment: 10, ukAccessibility: 2, communityFeel: 6, durationPreference: 8 },
    pitchlines: {
      earningPotential: "US dollar tips on American yachts. The earning ceiling is higher here.",
      careerDevelopment: "The biggest fleet concentration in the world. Careers are made here.",
      costOfLiving: "The earning outpaces the living costs. You'll bank more than you spend.",
      durationPreference: "American owners expect American work ethic. Long days, high standards.",
    },
  },
  {
    id: "caribbean", name: "Caribbean", tagline: "Winter charter season",
    region: "Caribbean \u00b7 Winter Season", guideSlug: "caribbean", jobsQuery: "season=sun&role=yacht-stewardess",
    seasonType: "sun", sunCategory: "yacht", emoji: "\ud83c\udfd5\ufe0f", gradient: YACHT_GRADIENT,
    attributes: { partyScore: 7, natureScore: 10, costOfLiving: 4, earningPotential: 10, conditionsQuality: 10, culturalDepth: 7, careerDevelopment: 9, ukAccessibility: 3, communityFeel: 5, durationPreference: 5 },
    pitchlines: {
      conditionsQuality: "Turquoise water, trade winds, and anchorages that make the Med look crowded.",
      earningPotential: "Charter season tips in the BVI are legendary for a reason.",
      natureScore: "A different island every week. Volcano hikes, reef snorkelling, empty beaches.",
      culturalDepth: "Each island is its own world. You won't get bored in a season.",
    },
  },
  {
    id: "uk-festivals", name: "UK Festival Circuit", tagline: "Summer weekends, UK wide",
    region: "United Kingdom \u00b7 Summer festivals",
    guideSlug: "festival-jobs-uk", jobsQuery: "season=festival",
    seasonType: "festival", emoji: "\ud83c\udfaa", gradient: FESTIVAL_GRADIENT,
    attributes: { partyScore: 10, natureScore: 6, costOfLiving: 6, earningPotential: 5, conditionsQuality: 5, culturalDepth: 6, careerDevelopment: 4, ukAccessibility: 10, communityFeel: 7, durationPreference: 2 },
    pitchlines: {
      partyScore: "A different crowd every weekend, all summer long.",
      ukAccessibility: "No flights, no visas. Just turn up and work.",
      durationPreference: "Short bursts of intense work with downtime between each one.",
    },
  },
];
